import { useState } from 'react';
import { Link } from 'react-router-dom';
import PanelLayout from '../components/PanelLayout';
import {
  dashboardStats,
  pendingRequests,
  activeConversations,
  newOpportunities,
  notifications,
} from '@/mocks/therapistPanel';

export default function DashboardPage() {
  const [notifOpen, setNotifOpen] = useState(false);

  const urgencyColor = (urgency: string) => {
    if (urgency === 'high') return 'bg-red-50 border-red-200 text-red-600';
    if (urgency === 'normal') return 'bg-amber-50 border-amber-200 text-amber-600';
    return 'bg-gray-50 border-gray-200 text-gray-500';
  };

  const statusBadge = (status: string) => {
    const map: Record<string, string> = {
      'görüşme başladı': 'bg-dark-teal-50 text-dark-teal-500',
      'telefon paylaşıldı': 'bg-soft-gold-100 text-soft-gold-700',
      beklemede: 'bg-amber-50 text-amber-600',
      'işe başladı': 'bg-green-50 text-green-600',
      'süreç kapandı': 'bg-gray-100 text-gray-500',
      'uygun bulunmadı': 'bg-gray-100 text-gray-500',
    };
    return map[status] || 'bg-gray-100 text-gray-500';
  };

  return (
    <PanelLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-deep-navy-500">Dashboard</h1>
            <p className="text-sm text-gray-500 mt-1">Hoş geldiniz, Ayşe. İşte günün özeti.</p>
          </div>
          <div className="relative">
            <button
              onClick={() => setNotifOpen(!notifOpen)}
              className="w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer relative"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-notification-3-line text-lg" />
              </span>
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-dark-teal-500 text-white text-[9px] font-bold flex items-center justify-center">
                2
              </span>
            </button>
            {notifOpen && (
              <div className="absolute right-0 top-12 w-80 bg-white rounded-xl shadow-soft-lg border border-gray-100 z-50">
                <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                  <span className="text-sm font-semibold text-deep-navy-500">Bildirimler</span>
                  <span className="text-xs text-gray-400">3 yeni</span>
                </div>
                <div className="max-h-72 overflow-y-auto">
                  {notifications.map((n) => (
                    <div
                      key={n.id}
                      className={`p-4 border-b border-gray-50 hover:bg-gray-50 transition-colors ${
                        !n.read ? 'bg-dark-teal-50/30' : ''
                      }`}
                    >
                      <p className="text-sm text-deep-navy-500">{n.message}</p>
                      <p className="text-xs text-gray-400 mt-1">{n.time}</p>
                    </div>
                  ))}
                </div>
                <div className="p-3 border-t border-gray-100">
                  <Link
                    to="/terapist-panel/bildirim-ayarlari"
                    className="text-xs font-medium text-dark-teal-500 hover:text-dark-teal-600"
                  >
                    Tüm bildirimleri gör
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-500">Profil Doluluk</span>
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-dark-teal-50 text-dark-teal-500">
                <i className="ri-user-line text-base" />
              </span>
            </div>
            <p className="text-2xl font-bold text-deep-navy-500">{dashboardStats.profileCompletion}%</p>
            <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-dark-teal-500 rounded-full transition-all"
                style={{ width: `${dashboardStats.profileCompletion}%` }}
              />
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-500">Görünürlük</span>
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-soft-gold-100 text-soft-gold-700">
                <i className="ri-eye-line text-base" />
              </span>
            </div>
            <p className="text-sm font-bold text-deep-navy-500 leading-tight">{dashboardStats.visibilityMode}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-500">Bekleyen Talep</span>
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-50 text-amber-600">
                <i className="ri-mail-send-line text-base" />
              </span>
            </div>
            <p className="text-2xl font-bold text-deep-navy-500">{dashboardStats.pendingRequests}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-500">Aktif Görüşme</span>
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-dark-teal-50 text-dark-teal-500">
                <i className="ri-chat-3-line text-base" />
              </span>
            </div>
            <p className="text-2xl font-bold text-deep-navy-500">{dashboardStats.activeConversations}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-500">Yeni Fırsat</span>
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-soft-gold-100 text-soft-gold-700">
                <i className="ri-briefcase-line text-base" />
              </span>
            </div>
            <p className="text-2xl font-bold text-deep-navy-500">{dashboardStats.newOpportunities}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-500">Profil Durumu</span>
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-green-50 text-green-600">
                <i className="ri-shield-check-line text-base" />
              </span>
            </div>
            <p className="text-sm font-bold text-green-600">{dashboardStats.profileStatus}</p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl border border-gray-100 p-4 mb-8">
          <h2 className="text-sm font-semibold text-deep-navy-500 mb-3">Hızlı Aksiyonlar</h2>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/terapist-panel/gorunurluk"
              className="px-4 py-2 rounded-lg bg-dark-teal-50 text-dark-teal-500 text-sm font-medium hover:bg-dark-teal-100 transition-colors"
            >
              Kariyer Modunu Değiştir
            </Link>
            <Link
              to="/terapist-panel/profilim"
              className="px-4 py-2 rounded-lg bg-gray-50 text-gray-600 text-sm font-medium hover:bg-gray-100 transition-colors"
            >
              Profilimi Güncelle
            </Link>
            <Link
              to="/terapist-panel/talepler"
              className="px-4 py-2 rounded-lg bg-amber-50 text-amber-600 text-sm font-medium hover:bg-amber-100 transition-colors"
            >
              Gelen Talepleri Gör
            </Link>
            <Link
              to="/terapist-panel/gorusmeler"
              className="px-4 py-2 rounded-lg bg-gray-50 text-gray-600 text-sm font-medium hover:bg-gray-100 transition-colors"
            >
              Aktif Görüşmeleri Aç
            </Link>
            <Link
              to="/terapist-panel/destek"
              className="px-4 py-2 rounded-lg bg-gray-50 text-gray-600 text-sm font-medium hover:bg-gray-100 transition-colors"
            >
              Destek Al
            </Link>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Pending Requests */}
          <div className="bg-white rounded-xl border border-gray-100">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-deep-navy-500">Bekleyen Talepler</h2>
              <Link
                to="/terapist-panel/talepler"
                className="text-xs font-medium text-dark-teal-500 hover:text-dark-teal-600"
              >
                Tümünü gör
              </Link>
            </div>
            <div className="divide-y divide-gray-50">
              {pendingRequests.map((req) => (
                <div key={req.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold text-deep-navy-500">{req.spaType}</span>
                        {req.verified && (
                          <span className="w-4 h-4 flex items-center justify-center text-dark-teal-500">
                            <i className="ri-verified-badge-fill text-xs" />
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">
                        {req.city} · {req.position} · {req.salaryBand}
                      </p>
                    </div>
                    <span
                      className={`px-2 py-1 rounded-full text-[10px] font-medium border ${urgencyColor(
                        req.urgency
                      )}`}
                    >
                      {req.hoursLeft} saat kaldı
                    </span>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <button className="px-3 py-1.5 rounded-md bg-dark-teal-500 text-white text-xs font-medium hover:bg-dark-teal-600 transition-colors cursor-pointer">
                      Kabul Et
                    </button>
                    <button className="px-3 py-1.5 rounded-md bg-gray-100 text-gray-600 text-xs font-medium hover:bg-gray-200 transition-colors cursor-pointer">
                      Uygun Değil
                    </button>
                    <button className="px-3 py-1.5 rounded-md bg-gray-100 text-gray-600 text-xs font-medium hover:bg-gray-200 transition-colors cursor-pointer">
                      Şu An Müsait Değilim
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Active Conversations */}
          <div className="bg-white rounded-xl border border-gray-100">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-deep-navy-500">Aktif Görüşmeler</h2>
              <Link
                to="/terapist-panel/gorusmeler"
                className="text-xs font-medium text-dark-teal-500 hover:text-dark-teal-600"
              >
                Tümünü gör
              </Link>
            </div>
            <div className="divide-y divide-gray-50">
              {activeConversations.map((conv) => (
                <Link
                  key={conv.id}
                  to={`/terapist-panel/gorusmeler/${conv.id}`}
                  className="p-4 block hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold text-deep-navy-500">{conv.spaType}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${statusBadge(conv.status)}`}>
                          {conv.status}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 truncate">{conv.lastMessage}</p>
                    </div>
                    <span className="text-[10px] text-gray-400 ml-3 shrink-0">{conv.lastDate}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* New Opportunities */}
        <div className="bg-white rounded-xl border border-gray-100 mt-6">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-deep-navy-500">Yeni Uygun İşletmeler</h2>
            <Link
              to="/terapist-panel/firsatlar"
              className="text-xs font-medium text-dark-teal-500 hover:text-dark-teal-600"
            >
              Tümünü gör
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-gray-50">
            {newOpportunities.slice(0, 3).map((opp) => (
              <div key={opp.id} className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-semibold text-deep-navy-500">{opp.spaType}</span>
                  {opp.verified && (
                    <span className="w-4 h-4 flex items-center justify-center text-dark-teal-500">
                      <i className="ri-verified-badge-fill text-xs" />
                    </span>
                  )}
                </div>
                <div className="space-y-1 text-xs text-gray-500">
                  <p>{opp.city}</p>
                  <p>{opp.workModel} · {opp.salaryBand}</p>
                  <p>SGK: {opp.ssg} · Konaklama: {opp.accommodation}</p>
                  <p className="text-dark-teal-500">{opp.expertise}</p>
                </div>
                <div className="mt-3 flex gap-2">
                  <button className="px-3 py-1.5 rounded-md bg-dark-teal-500 text-white text-xs font-medium hover:bg-dark-teal-600 transition-colors cursor-pointer">
                    Favoriye Al
                  </button>
                  <button className="px-3 py-1.5 rounded-md bg-gray-100 text-gray-600 text-xs font-medium hover:bg-gray-200 transition-colors cursor-pointer">
                    Gizle
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Profile Warning */}
        {dashboardStats.profileCompletion < 80 && (
          <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <span className="w-5 h-5 flex items-center justify-center text-amber-600 shrink-0 mt-0.5">
              <i className="ri-alert-line text-base" />
            </span>
            <div>
              <p className="text-sm font-medium text-amber-700">Profilinizi tamamlayın</p>
              <p className="text-xs text-amber-600 mt-1">
                Profil doluluk oranınız %{dashboardStats.profileCompletion}. Mini bio ve belgeler ekleyerek işletmelerin sizi daha iyi tanımasını sağlayabilirsiniz.
              </p>
              <Link
                to="/terapist-panel/profilim"
                className="inline-block mt-2 text-xs font-medium text-amber-700 underline"
              >
                Profilimi tamamla
              </Link>
            </div>
          </div>
        )}
      </div>
    </PanelLayout>
  );
}